import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/apiClient';
import { getDropdownDefaults } from '../config/dropdownDefaults';

const normalizeConfiguredOption = (option) => ({
    ...(option.metadata || {}),
    id: option.id,
    key: option.key || option.optionKey || option.id,
    text: option.text,
    value: option.value,
    sortOrder: option.sortOrder,
    isActive: option.isActive
});

export const mapConfiguredDropdownGroups = (groups = []) => {
    return groups.reduce((acc, group) => {
        acc[group.key] = (group.options || []).map(normalizeConfiguredOption);
        return acc;
    }, {});
};

export const mergeDropdownOptionsWithDefaults = (configuredOptions, defaultOptions) => {
    const defaultsByKey = new Map(defaultOptions.map(option => [String(option.key), option]));

    return configuredOptions.map(option => {
        const fallback = defaultsByKey.get(String(option.key)) || {};
        return {
            ...fallback,
            ...option,
            metadata: undefined
        };
    });
};

export const useDropdownOptions = (groupKey) => {
    const defaults = useMemo(() => getDropdownDefaults(groupKey), [groupKey]);
    const [options, setOptions] = useState(defaults);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const loadOptions = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await api.dropdownOptions.getAll(groupKey);
                const group = response.data?.groups?.find(item => item.key === groupKey);
                const configuredOptions = (group?.options || []).map(normalizeConfiguredOption);
                const nextOptions = configuredOptions.length
                    ? mergeDropdownOptionsWithDefaults(configuredOptions, defaults)
                    : defaults;

                if (!cancelled) {
                    setOptions(nextOptions);
                }
            } catch (err) {
                console.error(`Error loading dropdown options for ${groupKey}:`, err);
                if (!cancelled) {
                    setError(err);
                    setOptions(defaults);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadOptions();

        return () => {
            cancelled = true;
        };
    }, [groupKey, defaults]);

    return { options, loading, error };
};
